import { createFeatureSelector, createSelector } from '@ngrx/store';

import { Group, User, UserGroupAcl } from '@era/security-sdk';
import type { NamedUserGroupAcl, NamedUserGroupAclGroups, NamedUserGroupAclMembers } from '@rocksoft/shared';
import * as groupSelector from '../group/group.selector';
import { selectCurrentAclId } from '../router/acl-router.selector';
import { selectCurrentGroupId } from '../router/security-router.selector';
import * as userSelector from '../user/user.selector';
import * as fromUserGroupAcl from './user-group-acl.reducer';
import { UserGroupAclState } from './user-group-acl.state';

export const selectUserGroupAclState = createFeatureSelector<UserGroupAclState>('userGroupAcls');

export const selectUserGroupAclIds = createSelector(selectUserGroupAclState, fromUserGroupAcl.selectUserGroupAclIds);

export const selectAllUserGroupAcls = createSelector(selectUserGroupAclState, fromUserGroupAcl.selectAllUserGroupAcls);

export const selectAllUserGroupEntities = createSelector(
  selectUserGroupAclState,
  fromUserGroupAcl.selectUserGroupAclsEntities
);

export const selectUserGroupAclLoaded = createSelector(
  selectAllUserGroupAcls,
  (userGroupAcls: UserGroupAcl[]) => userGroupAcls && userGroupAcls.length > 0
);

export const selectUserGroupAclByResource = (resourceId: string) =>
  createSelector(selectAllUserGroupAcls, (userGroupAcls: UserGroupAcl[]) =>
    userGroupAcls ? userGroupAcls.find((x) => x.resourceId === resourceId) : undefined
  );

export const selectUserGroupAclByResourceWithName = (resourceId: string) =>
  createSelector(
    selectAllUserGroupAcls,
    userSelector.selectAllUsers,
    groupSelector.selectAllGroups,
    (userGroupAcls: UserGroupAcl[], users: User[], groups: Group[]) => {
      const findUserName = (id: string) => {
        const user = users.find((x) => x.id === id);
        return user ? user.displayName : undefined;
      };

      const findGroupName = (id: string) => {
        const group = groups.find((x) => x.id === id);
        return group ? group.displayName : undefined;
      };

      if (userGroupAcls && users && groups) {
        const acl = userGroupAcls.find((x) => x.resourceId === resourceId);
        if (acl) {
          const userGroupAclGroups: NamedUserGroupAclGroups[] = (acl.groups ?? []).map((x) => ({
            ...x,
            name: findGroupName(x.groupId) ?? '',
          }));
          const userGroupAclMembers: NamedUserGroupAclMembers[] = (acl.users ?? []).map((x) => ({
            ...x,
            name: findUserName(x.member) ?? '',
          }));

          const userGroupAcl: NamedUserGroupAcl = {
            ...acl,
            resourceId: acl.resourceId ?? '',
            groups: userGroupAclGroups,
            users: userGroupAclMembers,
          };

          return userGroupAcl;
        }
      }
      return undefined;
    }
  );

export const selectCurrentAclGroupsWithNamesByGroupIds = (groupIds: string[]) =>
  createSelector(
    selectAllUserGroupEntities,
    selectCurrentGroupId,
    groupSelector.selectAllGroups,
    (userGroupAcls, groupId: string, groups: Group[]) => {
      if (userGroupAcls && groups && groupIds) {
        const findGroupName = (id: string) => {
          const group = groups.find((x) => x.id === id);
          return group ? group.displayName : undefined;
        };

        const selectedAcl = userGroupAcls[groupId || 'Default-Group'];

        const userGroupAclsMembers = selectedAcl?.groups?.filter((x) => groupIds.includes(x.groupId));

        return userGroupAclsMembers
          ? userGroupAclsMembers.map((x) => ({ ...x, name: findGroupName(x.groupId) }))
          : undefined;
      }
      return undefined;
    }
  );

export const selectCurrentAclGroup = createSelector(
  selectAllUserGroupEntities,
  selectCurrentAclId,
  selectCurrentGroupId,
  (userGroupAcls, aclGroupId: string, groupId: string) => {
    if (userGroupAcls && aclGroupId) {
      // No groupId? Is group wide request.
      let selectedAcl = userGroupAcls[groupId || 'Default-Group'];
      const selectedGroup = selectedAcl ? selectedAcl.groups?.find((x) => x.groupId === aclGroupId) : undefined;

      // ResourceId is present on aclgroup: not group wide.
      if (selectedGroup) {
        if (selectedGroup.resourceId) {
          return selectedGroup;
        }
        // No resourceId found, is group wide aclgroup. Try getting from group wide acl instead.
        selectedAcl = userGroupAcls['Default-Group'];
        return selectedAcl ? selectedAcl.groups?.find((x) => x.groupId === aclGroupId) : undefined;
      }

      return undefined;
    }
    return undefined;
  }
);

export const selectCurrentAclMember = createSelector(
  selectAllUserGroupEntities,
  selectCurrentAclId,
  selectCurrentGroupId,
  (userGroupAcls, aclUserId: string, groupId: string) => {
    if (userGroupAcls && aclUserId) {
      // No groupId? Is group wide request.
      let selectedAcl = userGroupAcls[groupId || 'Default-Group'];
      const selectedMember = selectedAcl ? selectedAcl.users?.find((x) => x.member === aclUserId) : undefined;

      if (selectedMember) {
        // ResourceId is present on member: not group wide.
        if (selectedMember.resourceId) {
          return selectedMember;
        }
        // No resourceId found, is group wide member. Try getting from group wide acl instead.
        selectedAcl = userGroupAcls['Default-Group'];
        return selectedAcl ? selectedAcl.users?.find((x) => x.member === aclUserId) : undefined;
      }

      return undefined;
    }
    return undefined;
  }
);
